import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { getModuleAccessMap } from "@/lib/hasModuleAccess";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import DashboardStats, { DashStats } from "./dashboard/components/DashboardStats";
import DashboardAlerts, { AlertItem } from "./dashboard/components/DashboardAlerts";
import DashboardLeads, { LeadRow } from "./dashboard/components/DashboardLeads";
import DashboardJobs, { JobRow } from "./dashboard/components/DashboardJobs";
import DashboardPayments, { PaymentRow } from "./dashboard/components/DashboardPayments";
import DashboardModules, { ModuleRequest } from "./dashboard/components/DashboardModules";
import DashboardFinancial, { FinancialData } from "./dashboard/components/DashboardFinancial";
import DashboardProduction, { ProductionData, ProductionJob, ProductionAppointment } from "./dashboard/components/DashboardProduction";
import DashboardSalesFunnel, { SalesFunnelData, FunnelLead } from "./dashboard/components/DashboardSalesFunnel";
import DashboardAttention from "./dashboard/components/DashboardAttention";

/* ─── Types ──────────────────────────────────────────────────── */
type TenantInfo = {
  id: string;
  name: string;
  slug: string | null;
  plan_code: string;
  status: string;
};

type DashData = {
  stats: DashStats;
  financial: FinancialData;
  production: ProductionData;
  salesFunnel: SalesFunnelData;
  alerts: AlertItem[];
  leads: LeadRow[];
  jobs: JobRow[];
  payments: PaymentRow[];
  tenant: TenantInfo | null;
  modules: Record<string, boolean>;
  moduleRequests: ModuleRequest[];
};

const MODULE_IDS = [
  "photo_selection", "final_deliveries", "media_packages",
  "digital_invitations", "ai_sales_assistant", "custom_domain",
  "automations", "reminders", "appointments",
  "whatsapp_notifications", "signature_books",
];

/* ─── Greeting ───────────────────────────────────────────────── */
function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/* ─── Component ──────────────────────────────────────────────── */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState("");
  const [data, setData] = useState<DashData>({
    stats: {
      totalClients: 0, newLeads: 0, activeContracts: 0,
      totalCollected: 0, activeJobs: 0, pendingSelections: 0,
      pendingDeliveries: 0, pendingPayments: 0,
    },
    financial: {
      totalContractValue: 0, addOnsTotal: 0, paymentsReceived: 0,
      openBalance: 0, pendingPaymentsAmount: 0,
      thisMonthRevenue: 0, lastMonthRevenue: 0, collectionRate: 0,
    },
    production: {
      allJobs: [],
      upcomingJobs: [],
      upcomingAppts: [],
    },
    salesFunnel: { leads: [] },
    alerts: [],
    leads: [],
    jobs: [],
    payments: [],
    tenant: null,
    modules: {},
    moduleRequests: [],
  });

  const fetchDashboard = useCallback(async (tenantId: string) => {
    setError(null);
    try {
      /* ── 1. Module access map ── */
      const modules = await getModuleAccessMap(MODULE_IDS);

      /* ── Date windows ── */
      const now = new Date();
      const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const in48h   = new Date(now.getTime() + 48 * 60 * 60 * 1000);
      const nowIso  = now.toISOString();
      const in7Iso  = in7Days.toISOString();
      const in48Iso = in48h.toISOString();

      /* ── 2. Parallel data fetches ── */
      const [
        tenantRes,
        clientsRes,
        leadsRes,
        allLeadsRes,
        contractsRes,
        paymentsRes,
        allPaymentsRes,
        chargesRes,
        jobsRes,
        allJobsRes,
        upcomingJobsRes,
        apptsRes,
        photosRes,
        deliveriesRes,
        moduleRequestsRes,
      ] = await Promise.all([
        supabase.from("saas_tenants").select("id, name, slug, plan_code, status").eq("id", tenantId).maybeSingle(),
        supabase.from("clients").select("id", { count: "exact", head: true }).eq("tenant_id", tenantId),
        supabase.from("tenant_public_leads").select("id, name, email, phone, event_type, event_date, status, created_at").eq("tenant_id", tenantId).order("created_at", { ascending: false }).limit(8),
        // All leads for funnel (no limit)
        supabase.from("tenant_public_leads")
          .select("id, name, email, phone, event_type, event_date, status, source, created_at, converted_client_id")
          .eq("tenant_id", tenantId)
          .order("created_at", { ascending: false }),
        supabase.from("contracts").select("id, status, total_amount, balance_amount, deposit_amount, title, client_name, contract_number, event_date").eq("tenant_id", tenantId),
        // Recent 6 for the payments widget
        supabase.from("payments").select("id, client_name, payment_date, method, amount, status, contract_number").eq("tenant_id", tenantId).order("payment_date", { ascending: false }).limit(6),
        // All payments for financial calculations
        supabase.from("payments").select("id, amount, status, payment_date").eq("tenant_id", tenantId),
        // Contract charges (add-ons)
        supabase.from("contract_charges").select("id, amount, status").eq("tenant_id", tenantId),
        // Recent 6 for the jobs widget
        supabase.from("jobs").select("id, title, status, workflow_status, job_type, shoot_date, created_at, client_id, clients(full_name)").eq("tenant_id", tenantId).order("created_at", { ascending: false }).limit(6),
        // All jobs for production status breakdown
        supabase.from("jobs").select("id, title, job_type, status, workflow_status, shoot_date, updated_at, clients(full_name)").eq("tenant_id", tenantId).not("status", "eq", "cancelled"),
        // Upcoming shoots: shoot_date between now and +7 days
        supabase.from("jobs")
          .select("id, title, job_type, status, workflow_status, shoot_date, updated_at, clients(full_name)")
          .eq("tenant_id", tenantId)
          .gte("shoot_date", nowIso)
          .lte("shoot_date", in7Iso)
          .not("status", "eq", "cancelled")
          .order("shoot_date", { ascending: true }),
        // Upcoming appointments: start_at between now and +48h
        supabase.from("appointments")
          .select("id, title, appointment_type, start_at, location, clients(full_name)")
          .eq("tenant_id", tenantId)
          .gte("start_at", nowIso)
          .lte("start_at", in48Iso)
          .not("status", "eq", "cancelled")
          .order("start_at", { ascending: true }),
        // Pending selections
        modules.photo_selection
          ? supabase.from("jobs").select("id", { count: "exact", head: true }).eq("tenant_id", tenantId).eq("workflow_status", "waiting_client")
          : Promise.resolve({ count: 0 }),
        // Count active deliveries
        modules.final_deliveries
          ? supabase.from("job_deliveries").select("id", { count: "exact", head: true }).eq("tenant_id", tenantId).eq("is_active", true)
          : Promise.resolve({ count: 0 }),
        // Module activation requests
        supabase.from("tenant_module_requests")
          .select("id, module_id, status")
          .eq("tenant_id", tenantId)
          .in("status", ["pending", "requested"]),
      ]);

      const tenant = tenantRes.data as TenantInfo | null;
      if (tenant) setTenantName(tenant.name);

      const contracts = contractsRes.data ?? [];
      const payments  = (paymentsRes.data ?? []) as PaymentRow[];
      const allPayments = (allPaymentsRes.data ?? []) as { id: string; amount: number; status: string; payment_date: string }[];
      const charges   = (chargesRes.data ?? []) as { id: string; amount: number; status: string }[];

      const rawJobs = (jobsRes.data ?? []) as (JobRow & { clients?: { full_name: string } | null })[];
      const jobs: JobRow[] = rawJobs.map((j) => ({ ...j, client_name: j.clients?.full_name ?? null }));

      type RawProdJob = { id: string; title: string; job_type: string | null; status: string; workflow_status: string | null; shoot_date: string | null; updated_at: string | null; clients?: { full_name: string } | null };
      const mapProdJob = (j: RawProdJob): ProductionJob => ({
        id: j.id, title: j.title, job_type: j.job_type,
        status: j.status, workflow_status: j.workflow_status,
        shoot_date: j.shoot_date, updated_at: j.updated_at,
        client_name: j.clients?.full_name ?? null,
      });

      const allJobsData   = ((allJobsRes.data ?? []) as RawProdJob[]).map(mapProdJob);
      const upcomingJobs  = ((upcomingJobsRes.data ?? []) as RawProdJob[]).map(mapProdJob);

      type RawAppt = { id: string; title: string; appointment_type: string | null; start_at: string; location: string | null; clients?: { full_name: string } | null };
      const upcomingAppts: ProductionAppointment[] = ((apptsRes.data ?? []) as RawAppt[]).map((a) => ({
        id: a.id, title: a.title, appointment_type: a.appointment_type,
        start_at: a.start_at, location: a.location,
        client_name: a.clients?.full_name ?? null,
      }));

      const leads = (leadsRes.data ?? []) as LeadRow[];
      const allLeads = (allLeadsRes.data ?? []) as FunnelLead[];
      const moduleRequests = (moduleRequestsRes.data ?? []) as ModuleRequest[];

      /* ── 3. Derived stats ── */
      const CONFIRMED = ["confirmed", "paid", "completed"];

      const activeContracts = contracts.filter((c) =>
        ["active", "signed", "in_progress", "pending"].includes(c.status)
      ).length;

      const totalCollected = allPayments
        .filter((p) => CONFIRMED.includes(p.status))
        .reduce((s, p) => s + Number(p.amount), 0);

      const pendingPayments = allPayments.filter((p) => p.status === "pending").length;

      const activeJobs = allJobsData.filter((j) =>
        !["completed", "cancelled", "delivered"].includes(j.workflow_status ?? j.status)
      ).length;

      const newLeads = leads.filter((l) => l.status === "new").length;

      /* ── 4. Financial metrics ── */
      const thisYear  = now.getFullYear();
      const thisMonth = now.getMonth();
      const lastMonthDate = new Date(thisYear, thisMonth - 1, 1);
      const lastYear  = lastMonthDate.getFullYear();
      const lastMonth = lastMonthDate.getMonth();

      const thisMonthRevenue = allPayments
        .filter((p) => {
          if (!CONFIRMED.includes(p.status)) return false;
          const d = new Date(p.payment_date);
          return d.getFullYear() === thisYear && d.getMonth() === thisMonth;
        })
        .reduce((s, p) => s + Number(p.amount), 0);

      const lastMonthRevenue = allPayments
        .filter((p) => {
          if (!CONFIRMED.includes(p.status)) return false;
          const d = new Date(p.payment_date);
          return d.getFullYear() === lastYear && d.getMonth() === lastMonth;
        })
        .reduce((s, p) => s + Number(p.amount), 0);

      const totalContractValue = contracts.reduce((s, c) => s + Number(c.total_amount ?? 0), 0);

      const addOnsTotal = charges
        .filter((ch) => !["cancelled", "void", "voided"].includes(ch.status ?? ""))
        .reduce((s, ch) => s + Number(ch.amount ?? 0), 0);

      const openBalance = contracts
        .filter((c) => ["active", "signed", "in_progress", "pending"].includes(c.status))
        .reduce((s, c) => s + Number(c.balance_amount ?? 0), 0);

      const pendingPaymentsAmount = allPayments
        .filter((p) => p.status === "pending")
        .reduce((s, p) => s + Number(p.amount), 0);

      const collectionRate = totalContractValue > 0
        ? Math.min(100, (totalCollected / totalContractValue) * 100)
        : 0;

      /* ── 5. Alerts ── */
      const alerts: AlertItem[] = [];
      const nowMs    = Date.now();
      const FIVE_DAYS = 5 * 24 * 60 * 60 * 1000;
      const TWO_DAYS  = 2 * 24 * 60 * 60 * 1000;

      // New leads
      if (newLeads > 0) {
        alerts.push({
          id: "new-leads",
          severity: "info",
          icon: "ri-user-received-line",
          title: `${newLeads} new lead${newLeads > 1 ? "s" : ""} waiting for contact`,
          desc: "Respond quickly to increase booking rate",
          href: "/admin/leads",
          action: "View Leads",
        });
      }

      // Jobs waiting_client > 5 days (use workflow_status)
      allJobsData
        .filter((j) => (j.workflow_status ?? j.status) === "waiting_client")
        .forEach((j) => {
          const ref = j.updated_at ?? nowIso;
          const diff = nowMs - new Date(ref).getTime();
          if (diff > FIVE_DAYS) {
            const days = Math.floor(diff / (24 * 60 * 60 * 1000));
            alerts.push({
              id: `wc-${j.id}`,
              severity: "warning",
              icon: "ri-time-line",
              title: `"${j.title}" waiting client — ${days}d`,
              desc: j.client_name ?? "No client",
              href: `/admin/jobs/${j.id}`,
              action: "View Job",
            });
          }
        });

      // Upcoming shoots within 7 days
      if (upcomingJobs.length > 0) {
        const todayCount = upcomingJobs.filter((j) => {
          const d = new Date(j.shoot_date!.includes("T") ? j.shoot_date! : `${j.shoot_date}T00:00:00`);
          d.setHours(0, 0, 0, 0);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          return d.getTime() === today.getTime();
        }).length;

        if (todayCount > 0) {
          alerts.push({
            id: "shoots-today",
            severity: "warning",
            icon: "ri-camera-line",
            title: `${todayCount} shoot${todayCount > 1 ? "s" : ""} scheduled for today`,
            desc: upcomingJobs
              .filter((j) => {
                const d = new Date(j.shoot_date!.includes("T") ? j.shoot_date! : `${j.shoot_date}T00:00:00`);
                d.setHours(0, 0, 0, 0);
                const today = new Date(); today.setHours(0, 0, 0, 0);
                return d.getTime() === today.getTime();
              })
              .map((j) => j.title)
              .join(", "),
            href: "/admin/jobs",
            action: "View",
          });
        } else {
          const next = upcomingJobs[0];
          const ms = new Date(next.shoot_date!.includes("T") ? next.shoot_date! : `${next.shoot_date}T00:00:00`).getTime() - nowMs;
          const daysAway = Math.ceil(ms / (1000 * 60 * 60 * 24));
          if (daysAway <= 2) {
            alerts.push({
              id: `shoot-soon-${next.id}`,
              severity: "info",
              icon: "ri-camera-line",
              title: `Shoot in ${daysAway === 1 ? "1 day" : `${daysAway} days`}: ${next.title}`,
              desc: next.client_name ?? "",
              href: `/admin/jobs/${next.id}`,
              action: "View Job",
            });
          }
        }
      }

      // Upcoming appointments within 48h
      if (upcomingAppts.length > 0) {
        const urgentAppts = upcomingAppts.filter((a) =>
          new Date(a.start_at).getTime() - nowMs < TWO_DAYS
        );
        if (urgentAppts.length > 0) {
          alerts.push({
            id: "appts-soon",
            severity: "info",
            icon: "ri-calendar-check-line",
            title: `${urgentAppts.length} appointment${urgentAppts.length > 1 ? "s" : ""} in the next 48 hours`,
            desc: urgentAppts.map((a) => a.title).join(", "),
            href: "/admin/appointments",
            action: "View",
          });
        }
      }

      // Contracts without deposit
      contracts
        .filter((c) => ["active", "signed"].includes(c.status) && Number(c.deposit_amount) <= 0)
        .slice(0, 3)
        .forEach((c) => {
          alerts.push({
            id: `nd-${c.id}`,
            severity: "danger",
            icon: "ri-error-warning-line",
            title: "Contract with no deposit received",
            desc: `${c.title} — ${c.client_name ?? c.contract_number}`,
            href: `/admin/contracts/${c.id}`,
            action: "View Contract",
          });
        });

      // Pending payments
      if (pendingPayments > 0) {
        alerts.push({
          id: "pending-pays",
          severity: "warning",
          icon: "ri-money-dollar-circle-line",
          title: `${pendingPayments} payment${pendingPayments > 1 ? "s" : ""} pending confirmation`,
          desc: `$${Math.round(pendingPaymentsAmount).toLocaleString()} total awaiting confirmation`,
          href: "/admin/payments",
          action: "Review",
        });
      }

      // Pending selections
      const pendingSelections = modules.photo_selection ? (photosRes.count ?? 0) : 0;
      if (pendingSelections > 0) {
        alerts.push({
          id: "pending-selections",
          severity: "info",
          icon: "ri-image-2-line",
          title: `${pendingSelections} job${pendingSelections > 1 ? "s" : ""} awaiting client photo selection`,
          desc: "Clients haven't reviewed their gallery yet",
          href: "/admin/jobs",
          action: "View Jobs",
        });
      }

      setData({
        stats: {
          totalClients: clientsRes.count ?? 0,
          newLeads,
          activeContracts,
          totalCollected,
          activeJobs,
          pendingSelections,
          pendingDeliveries: modules.final_deliveries ? (deliveriesRes.count ?? 0) : 0,
          pendingPayments,
        },
        financial: {
          totalContractValue,
          addOnsTotal,
          paymentsReceived: totalCollected,
          openBalance,
          pendingPaymentsAmount,
          thisMonthRevenue,
          lastMonthRevenue,
          collectionRate,
        },
        production: {
          allJobs:       allJobsData,
          upcomingJobs,
          upcomingAppts,
        },
        salesFunnel: { leads: allLeads },
        alerts,
        leads,
        jobs,
        payments,
        tenant,
        modules,
        moduleRequests,
      });
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error loading dashboard.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }

      const { data: userData } = await supabase.auth.getUser();
      if (userData?.user) {
        const { data: sa } = await supabase
          .from("saas_super_admins")
          .select("id")
          .eq("user_id", userData.user.id)
          .maybeSingle();
        if (sa && !localStorage.getItem("impersonated_tenant_id")) {
          setLoading(false);
          return;
        }
      }

      const tenantId = await getTenantId();
      if (tenantId) {
        await fetchDashboard(tenantId);
      } else {
        setError("No studio account found. Please contact support.");
        setLoading(false);
      }
    };
    init();
  }, [navigate, fetchDashboard]);

  /* ── Quick actions ── */
  const QUICK_ACTIONS = [
    { to: "/admin/clients",      icon: "ri-user-add-line",              label: "New Client",   color: "hover:border-teal-400/40 hover:text-teal-600" },
    { to: "/admin/contracts",    icon: "ri-file-add-line",              label: "New Contract", color: "hover:border-[#C8A96E]/40 hover:text-[#a6853e]" },
    { to: "/admin/jobs",         icon: "ri-camera-2-line",              label: "New Job",      color: "hover:border-amber-400/40 hover:text-amber-600" },
    { to: "/admin/leads",        icon: "ri-user-received-line",         label: "View Leads",   color: "hover:border-[#C8A96E]/40 hover:text-[#a6853e]" },
    { to: "/admin/payments",     icon: "ri-money-dollar-circle-line",   label: "Add Payment",  color: "hover:border-emerald-400/40 hover:text-emerald-600" },
    { to: "/admin/appointments", icon: "ri-calendar-check-line",        label: "Appointments", color: "hover:border-teal-400/40 hover:text-teal-600" },
  ];

  return (
    <AdminLayout>
      <AdminHeader
        title="Dashboard"
        subtitle={`${getGreeting()}${tenantName ? `, ${tenantName}` : ""} — here's your studio overview`}
        actions={
          <button
            onClick={() => { setLoading(true); getTenantId().then((tid) => { if (tid) fetchDashboard(tid); }); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#0f172a] hover:bg-slate-50 transition-colors cursor-pointer"
            title="Refresh"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7 space-y-6">
        {loading ? (
          <LoadingState text="Loading dashboard…" />
        ) : error ? (
          <ErrorState
            title="Failed to load dashboard"
            message={error}
            onRetry={() => { setLoading(true); navigate(0); }}
            showLogin
          />
        ) : (
          <>
            {/* ── Quick Actions ── */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-300 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap mr-1">Quick</span>
              {QUICK_ACTIONS.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className={`flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg border border-slate-200 text-xs text-slate-400 transition-all cursor-pointer whitespace-nowrap ${a.color}`}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${a.icon} text-xs`}></i>
                  </span>
                  {a.label}
                </Link>
              ))}
            </div>

            {/* ── Stats ── */}
            <DashboardStats stats={data.stats} modules={data.modules} />

            {/* ── Production Overview ── */}
            <DashboardProduction production={data.production} />

            {/* ── Needs Attention ── */}
            <DashboardAttention />

            {/* ── Financial Summary ── */}
            <DashboardFinancial financial={data.financial} />

            {/* ── Sales Funnel ── */}
            <DashboardSalesFunnel data={data.salesFunnel} />

            {/* ── Alerts ── */}
            <DashboardAlerts alerts={data.alerts} />

            {/* ── Main 3-col grid: Leads + Jobs + Payments ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <DashboardLeads leads={data.leads} />
              <DashboardJobs jobs={data.jobs} />
              <DashboardPayments payments={data.payments} />
            </div>

            {/* ── Module Status ── */}
            <DashboardModules modules={data.modules} moduleRequests={data.moduleRequests} />

            {/* ── Tenant info strip ── */}
            {data.tenant && (
              <div className="flex items-center gap-4 px-5 py-3.5 bg-white rounded-xl border border-slate-200 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <i className="ri-building-line text-xs"></i>
                  </span>
                  <span className="text-slate-600 text-sm font-medium">{data.tenant.name}</span>
                </div>
                <span className="text-slate-300">·</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#C8A96E]/15 text-[#a6853e] capitalize">{data.tenant.plan_code}</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 capitalize">{data.tenant.status}</span>
                {data.tenant.slug && (
                  <>
                    <span className="text-slate-300">·</span>
                    <a
                      href={`/s/${data.tenant.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-slate-400 hover:text-[#a6853e] text-xs transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-xs"></i>
                      /s/{data.tenant.slug}
                    </a>
                  </>
                )}
                <div className="ml-auto">
                  <Link
                    to="/admin/settings/branding"
                    className="flex items-center gap-1.5 text-slate-300 hover:text-slate-500 text-xs transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-settings-3-line text-xs"></i>
                    Settings
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
